import * as v from "valibot";

import { defaultAltitude, defaultGlideSlope } from "./configuration";

export type Point = readonly [longitude: number, latitude: number];

const position = v.pipe(
  v.tuple([v.number(), v.number(), v.number()]),
  v.readonly(),
);
export type Position = v.InferOutput<typeof position>;

export type Orientation = readonly [yaw: number, pitch: number, roll: number];

export type State = {
  time: number;
  bootTime: number;
  simulation: boolean;
  flying: boolean;
  armed: boolean;
  position: Position;
  orientation: Orientation;
  target: Position;
  heading: number;
  batteryVoltage: number;
  airspeed: number;
  path: Position[];
};

export const cargo = v.object({
  name: v.string(),
  weight: v.number(),
  location: v.number(),
});

export type Cargo = v.InferOutput<typeof cargo>;

export const drop = v.object({
  start: position,
  end: position,
  drop: position,
  bend1: v.number(),
  bend2: v.number(),
  dropAltitude: v.number(),
  dropSpeed: v.number(),
  dropDirection: v.number(),
  windSpeed: v.number(),
  windDirection: v.number(),
  glideSlope: v.number(),
  bidirectional: v.boolean(),
  emptyWeight: v.number(),
  cargo: v.array(cargo),
});
export type Drop = v.InferOutput<typeof drop>;

export const defaultDrop: Drop = {
  start: [0, 0, 0],
  end: [0, 0, 0],
  drop: [0, 0, 0],
  bend1: 0,
  bend2: 0,
  glideSlope: defaultGlideSlope,
  dropAltitude: defaultAltitude,
  dropSpeed: 0,
  dropDirection: 0,
  windSpeed: 0,
  windDirection: 0,
  bidirectional: false,
  emptyWeight: 493.5,
  cargo: [
    {
      name: "Steel plates (8)",
      weight: 300,
      location: 16.75,
    },
    {
      name: "Steel plates (11)",
      weight: 412.5,
      location: 48.75,
    },
  ],
};

export const modes = [
  "introduction",
  "weight",
  "edit",
  "start",
  "end",
  "drop",
  "preflight",
] as const;
export type Mode = (typeof modes)[number];

export type Bounds = [
  minLongitude: number,
  minLatitude: number,
  maxLongitude?: number,
  maxLatitude?: number,
];

export const boundsCenter = ([
  minLongitude,
  minLatitude,
  maxLongitude = minLongitude,
  maxLatitude = minLatitude,
]: Bounds) =>
  [
    0.5 * (maxLongitude + minLongitude),
    0.5 * (maxLatitude + minLatitude),
  ] satisfies Point;

export const bounds = (positions: Position[]): Bounds => {
  const longitudes = positions.map(([lon]) => lon);
  const latitudes = positions.map(([, lat]) => lat);
  return [
    Math.min(...longitudes),
    Math.min(...latitudes),
    Math.max(...longitudes),
    Math.max(...latitudes),
  ];
};
